'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { CalendarCall } from '@/store/slices/calendarSlice';
import { fetchRecentCalls } from '@/lib/calls/recentCalls';

export type InventoryPhase = 'idle' | 'loading' | 'ready' | 'error';

interface Inventory {
  userId: string | undefined;
  rows: CalendarCall[];
  phase: InventoryPhase;
}

/**
 * Per-user recent-call inventory fetched from the server on demand. Only the latest `load()`
 * is applied (a sequence guard), and a user change drops the previous user's rows.
 */
export function useRecentCallInventory(userId: string | undefined) {
  const [inv, setInv] = useState<Inventory>({ userId, rows: [], phase: 'idle' });
  const seq = useRef(0);

  useEffect(() => {
    seq.current += 1; // anything in flight belongs to the previous user
    setInv({ userId, rows: [], phase: 'idle' });
  }, [userId]);

  const load = useCallback(() => {
    if (!userId) return;
    const mine = ++seq.current;
    setInv((prev) => ({ userId, rows: prev.userId === userId ? prev.rows : [], phase: 'loading' }));
    fetchRecentCalls().then(
      (rows) => { if (mine === seq.current) setInv({ userId, rows, phase: 'ready' }); },
      () => { if (mine === seq.current) setInv((prev) => ({ ...prev, userId, phase: 'error' })); },
    );
  }, [userId]);

  const current = inv.userId === userId;
  return { rows: current ? inv.rows : [], phase: current ? inv.phase : 'idle', load };
}
